import * as React from 'react';
import { t, tryT } from '@reos/i18n';
import { canSeeMenuItem } from '../lib/capabilities.js';
import { useSession } from '../stores/session.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  approveReport,
  // Krok REV1: nacteni vyhrad brany u jedne objednavky (rozbaleni na klik).
  fetchAcceptanceMissing,
  listPendingReleases,
  releaseReport,
  reportPreviewUrl,
  returnReport,
  type PendingReleaseItem,
} from '../lib/reports-release-api.js';
// Krok REV1: kod -> veta. SSOT je v kontextu (`formatAcceptanceMissing`), proto
// podcesta v `exports` - hodnotovy import z barrelu `@reos/duediligence` by do
// prohlizecoveho bundlu pritahl `@reos/db` -> `node:async_hooks`.
import { formatAcceptanceMissing } from '@reos/duediligence/acceptance-messages';
import type { AcceptanceMissingItem } from '@reos/duediligence';
import type { QueueSectionProps } from '../lib/approval-queue-section.js';

/**
 * Krok A5 bod 1 — FRONTA „KE SCHVALENI" (sekce na stavajici strance Schvaleni).
 *
 * Co Revizor u kazdeho reportu vidi: nahled PDF, PROC ho ma videt clovek
 * (spoustece politiky), vysledek Oponenta a Kontroly konzistence (kody z A2),
 * kolik otazek prebira clovek (H1) a shrnuti „na co se podivat" od lokalniho
 * modelu. Dve akce: uvolnit zakaznikovi, nebo vratit k prepracovani s duvodem.
 *
 * Zadny text natvrdo (CLAUDE.md pravidlo 5) - vsechno pres `t()`; strojove kody
 * z API se prekladaji tim samym katalogem, ze ktereho je tiskne worker.
 * Povinny duvod vynucuje BACKEND (order-service.ts), `window.prompt` je jen
 * minimalni UI - stejny vzor jako fronta objednavek z C3.
 */
export function ReportReleaseQueue({ inQueue = false, onCount }: QueueSectionProps = {}): React.JSX.Element | null {
  // POZOR: selektor musi vracet STABILNI referenci. `s.user?.roles ?? []` by
  // pri kazdem renderu vratil nove pole a `useSyncExternalStore` by se zacyklil
  // („Maximum update depth exceeded") - proto se vybira uzivatel a role se
  // odvozuji az mimo selektor.
  const user = useSession((s) => s.user);
  const allowed = canSeeMenuItem(user?.roles ?? [], 'commerce.report.release');
  const [items, setItems] = React.useState<readonly PendingReleaseItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  /**
   * Krok REV1 bod 1: VYHRADY BRANY U JEDNE OBJEDNAVKY, rozbalene na klik.
   *
   * Nacita se az na vyzadani, ne pri mountu fronty: polozek je ve fronte
   * vic a kazda by znamenala dalsi dotaz na `GET /v1/audit-runs/:id`
   * (fronta sama o behu nevi nic nez jeho id). `'loading'` / `'error'`
   * jsou plnohodnotne stavy — revizor musi poznat rozdil mezi „nic
   * nevytkla" a „nepodarilo se zjistit".
   */
  const [missingOf, setMissingOf] = React.useState<
    Readonly<Record<string, readonly AcceptanceMissingItem[] | 'loading' | 'error'>>
  >({});

  const toggleMissing = React.useCallback(async (item: PendingReleaseItem): Promise<void> => {
    const shown = missingOf[item.orderId];
    if (shown !== undefined) {
      setMissingOf((prev) => {
        const next = { ...prev };
        delete next[item.orderId];
        return next;
      });
      return;
    }
    if (item.auditRunId === null) return;
    setMissingOf((prev) => ({ ...prev, [item.orderId]: 'loading' }));
    try {
      const missing = await fetchAcceptanceMissing(item.auditRunId);
      setMissingOf((prev) => ({ ...prev, [item.orderId]: missing }));
    } catch {
      setMissingOf((prev) => ({ ...prev, [item.orderId]: 'error' }));
    }
  }, [missingOf]);

  const load = React.useCallback(async () => {
    try {
      setItems(await listPendingReleases());
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.reportReview.error', { locale: 'cs' }));
    }
  }, []);

  React.useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  /*
   * Pocet hlasi stranka vys, az kdyz je znamy — `null` je „jeste nevim".
   * Kdo frontu nesmi videt, ma cekat na rozhodnuti NULA veci; bez teto vetve
   * by stranka na prazdny stav cekala navzdy (fronta by nikdy neodpovedela).
   */
  React.useEffect(() => {
    if (!allowed) onCount?.(0);
    else if (items !== null) onCount?.(items.length);
  }, [allowed, items, onCount]);

  /**
   * Kdo nesmi uvolnovat, nevidi ani frontu (a nedostane 403 na obrazovce, kterou
   * ma kvuli jine sekci otevrenou legitimne).
   */
  if (!allowed) return null;

  /**
   * Krok REV1: tri rozhodnuti, ne dve. `approve` je uvolneni u objednavky,
   * pro kterou klientsky dokument JESTE NEVZNIKL (branu uplnosti nebo branu
   * vydani neprosel) - do REV1 na ni fronta nabizela jen vypnute tlacitko
   * a revizor s ni nemohl udelat nic.
   */
  const PROMPT_KEY = {
    release: 'webAdmin.reportReview.releasePrompt',
    approve: 'webAdmin.reportReview.approvePrompt',
    return: 'webAdmin.reportReview.returnPrompt',
  } as const;
  const SUCCESS_KEY = {
    release: 'webAdmin.reportReview.actionSuccessRelease',
    approve: 'webAdmin.reportReview.actionSuccessApprove',
    return: 'webAdmin.reportReview.actionSuccessReturn',
  } as const;

  async function decide(
    item: PendingReleaseItem,
    action: 'release' | 'approve' | 'return',
  ): Promise<void> {
    const answer = window.prompt(t(PROMPT_KEY[action], { locale: 'cs' }));
    // Zruseny dialog = zadna akce. U vraceni je duvod POVINNY, takze prazdna
    // odpoved taky nic neposila - jinak by backend vratil 400 a uzivatel by
    // netusel, co udelal spatne.
    if (answer === null) return;
    if (action === 'return' && answer.trim().length < 3) return;
    setBusyId(item.orderId);
    setError(null);
    setNotice(null);
    try {
      if (action === 'release') await releaseReport(item.orderId, answer);
      else if (action === 'approve') await approveReport(item.orderId, answer);
      else await returnReport(item.orderId, answer);
      setNotice(t(SUCCESS_KEY[action], { locale: 'cs' }));
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.reportReview.actionError', { locale: 'cs' }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="mb-8" data-testid="report-release-queue">
      {/* Krok UXF4 bod 8 (#29): oddil obrazovky Schvaleni, ne samostatna
          stranka — jeden `h1` dava `PageHead`, fronty byly `h2`.
          Krok UXF5 bod 4: v jedne fronte uz nadpis ani perex nestoji vubec. */}
      {inQueue ? null : (
        <>
          <h2 className="mb-1 text-lg font-semibold">{t('webAdmin.reportReview.title', { locale: 'cs' })}</h2>
          <p className="mb-4 text-sm text-gray-600">{t('webAdmin.reportReview.description', { locale: 'cs' })}</p>
        </>
      )}

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="mb-2 text-sm text-green-700" data-testid="report-release-notice">
          {notice}
        </p>
      )}

      {items === null ? (
        inQueue ? null : <p>{t('webAdmin.reportReview.loading', { locale: 'cs' })}</p>
      ) : items.length === 0 ? (
        inQueue ? null : (
          <p data-testid="report-release-empty">{t('webAdmin.reportReview.empty', { locale: 'cs' })}</p>
        )
      ) : (
        <table className="w-full text-sm" data-testid="report-release-table">
          <thead>
            <tr className="text-left">
              <th className="pr-4">{t('webAdmin.reportReview.columnOrderNumber', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.reportReview.columnAddress', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.reportReview.columnPropertyKind', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.reportReview.columnTriggers', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.reportReview.columnSummary', { locale: 'cs' })}</th>
              <th className="pr-4">{t('webAdmin.reportReview.columnCreatedAt', { locale: 'cs' })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.reviewId} className="border-t align-top" data-testid={`report-release-row-${item.orderNumber}`}>
                <td className="py-2 pr-4">{item.orderNumber}</td>
                <td className="py-2 pr-4">{item.addressLabel}</td>
                <td className="py-2 pr-4">{propertyKindLabel(item.propertyKind)}</td>
                <td className="py-2 pr-4">
                  <ul>
                    {item.triggerCodes.map((code) => (
                      <li key={code}>{triggerLabel(code)}</li>
                    ))}
                    {item.aiCodes.map((code) => (
                      <li key={code} className="text-amber-700">
                        {aiCodeLabel(code)}
                      </li>
                    ))}
                    {item.needsReviewReason !== null && (
                      <li className="text-gray-600">{item.needsReviewReason}</li>
                    )}
                  </ul>
                </td>
                <td className="py-2 pr-4 whitespace-pre-line">
                  {item.reviewSummary ?? t('webAdmin.reportReview.summaryMissing', { locale: 'cs' })}
                  {item.openHumanTasks > 0 && (
                    <div className="text-gray-600">
                      {t('webAdmin.reportReview.openHumanTasks', {
                        locale: 'cs',
                        values: { count: item.openHumanTasks },
                      })}
                    </div>
                  )}
                  {/*
                    Krok REV1 bod 1: CO BRANE CHYBELO, lidsky. Do REV1 tu stály
                    jen kody spoustecu a `needsReviewReason` - revizor z nich
                    nepoznal, CO ma doplnit nebo oznacit „nezjisteno s duvodem".
                    Veta vznika v kontextu (`formatAcceptanceMissing`), tady se
                    jen vypisuje; bez behu auditu neni co vztahovat.
                  */}
                  {item.auditRunId === null ? (
                    <div className="text-gray-500" data-testid={`report-release-missing-norun-${item.orderNumber}`}>
                      {t('webAdmin.reportReview.missingNoRun', { locale: 'cs' })}
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="rh-btn rh-btn--ghost mt-1"
                        onClick={() => void toggleMissing(item)}
                        data-testid={`report-release-missing-toggle-${item.orderNumber}`}
                      >
                        {t(
                          missingOf[item.orderId] === undefined
                            ? 'webAdmin.reportReview.missingToggle'
                            : 'webAdmin.reportReview.missingHide',
                          { locale: 'cs' },
                        )}
                      </button>
                      {missingOf[item.orderId] !== undefined && (
                        <div
                          className="mt-1 text-gray-700"
                          data-testid={`report-release-missing-${item.orderNumber}`}
                        >
                          {missingOf[item.orderId] === 'loading'
                            ? t('webAdmin.reportReview.missingLoading', { locale: 'cs' })
                            : missingOf[item.orderId] === 'error'
                              ? t('webAdmin.reportReview.missingError', { locale: 'cs' })
                              : missingList(missingOf[item.orderId]).length === 0
                                ? t('webAdmin.reportReview.missingEmpty', { locale: 'cs' })
                                : (
                                    <ul className="list-disc pl-4">
                                      {missingList(missingOf[item.orderId]).map((entry, index) => (
                                        <li key={`${entry.code}-${index}`}>{missingLabel(entry)}</li>
                                      ))}
                                    </ul>
                                  )}
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="py-2 pr-4">{item.createdAt}</td>
                <td className="py-2 whitespace-nowrap">
                  {item.exportId !== null && item.reportReady ? (
                    <a
                      className="rh-btn mr-2"
                      href={reportPreviewUrl(item.exportId)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`report-release-preview-${item.orderNumber}`}
                    >
                      {t('webAdmin.reportReview.preview', { locale: 'cs' })}
                    </a>
                  ) : (
                    <span className="mr-2 text-gray-500">
                      {t('webAdmin.reportReview.previewMissing', { locale: 'cs' })}
                    </span>
                  )}
                  <button
                    type="button"
                    className="rh-btn rh-btn--primary mr-2"
                    // Bez hotoveho PDF nejde uvolnit nic - backend to odmitne
                    // (`ReportNotReadyError`), tak to tlacitko nema predstirat.
                    disabled={busyId === item.orderId || !item.reportReady}
                    onClick={() => void decide(item, 'release')}
                    data-testid={`report-release-release-${item.orderNumber}`}
                  >
                    {t('webAdmin.reportReview.actionRelease', { locale: 'cs' })}
                  </button>
                  {/*
                    Krok REV1: vypnute „Uvolnit zakaznikovi" vyse zustava - uvolnit
                    se opravdu nedá. Chybela ale druha cesta, takze zaplacena
                    objednavka, pro kterou dokument nikdy nevznikne, visela nadobro.
                    Proto VEDLE nej (ne misto nej) „Schvalit vydani": report se
                    vykresli v rozsahu produktu a odejde sam. Uz schvalena
                    objednavka tlacitko nema - druhy klik by nic nezmenil
                    (schvaleni je idempotentni) a jen by svadel k domnence,
                    ze prvni nezabral.
                  */}
                  {!item.reportReady &&
                    (item.releaseApproved ? (
                      <span
                        className="mr-2 text-green-700"
                        data-testid={`report-release-approved-${item.orderNumber}`}
                      >
                        {t('webAdmin.reportReview.approvedWaiting', { locale: 'cs' })}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="rh-btn rh-btn--primary mr-2"
                        disabled={busyId === item.orderId}
                        onClick={() => void decide(item, 'approve')}
                        data-testid={`report-release-approve-${item.orderNumber}`}
                      >
                        {t('webAdmin.reportReview.actionApprove', { locale: 'cs' })}
                      </button>
                    ))}
                  <button
                    type="button"
                    className="rh-btn"
                    disabled={busyId === item.orderId}
                    onClick={() => void decide(item, 'return')}
                    data-testid={`report-release-return-${item.orderNumber}`}
                  >
                    {t('webAdmin.reportReview.actionReturn', { locale: 'cs' })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Strojovy kod -> veta. Neznamy kod se vypise, jak prisel: nova verze API nesmi
 * shodit obrazovku (poucení N6 z H1 - neznamy klic v `t()` znamenal pad exportu).
 */
function triggerLabel(code: string): string {
  const key = `commerce.release.trigger.${code}` as 'commerce.release.trigger.PILOT_FIRST_N';
  // `t()` na neznamem klici VYHAZUJE (poucení N6 z H1: neznamy klic shodil
  // cely export) - proto `tryT` a fallback na samotny kod.
  return tryT(key, { locale: 'cs' }) ?? code;
}

function aiCodeLabel(code: string): string {
  const key = `ai.reviewer.aiCode.${code}` as 'ai.reviewer.aiCode.OPPONENT_UNAVAILABLE';
  return tryT(key, { locale: 'cs' }) ?? code;
}

function propertyKindLabel(kind: string): string {
  const key = `commerce.release.propertyKind.${kind}` as 'commerce.release.propertyKind.HOUSE';
  return tryT(key, { locale: 'cs' }) ?? kind;
}

/**
 * Krok REV1: zuzeni stavu rozbaleneho vypisu na samotna data. `'loading'`
 * a `'error'` resi volajici pred timhle vetvenim, takze tady uz jsou jen
 * polozky — funkce existuje proto, aby to TypeScript videl taky.
 */
function missingList(
  state: readonly AcceptanceMissingItem[] | 'loading' | 'error' | undefined,
): readonly AcceptanceMissingItem[] {
  return typeof state === 'string' || state === undefined ? [] : state;
}

/**
 * Veta o jedne vytce brany. Text sklada SSOT v kontextu
 * (`formatAcceptanceMissing`, i18n katalog `dd.acceptance.missing.*`), tady se
 * jen hlida poucení N6: ten formatter jde pres `t()`, ktere na neznamem klici
 * VYHAZUJE, a novejsi engine muze poslat kod, ktery katalog jeste nezna. Pad
 * jedne vytky nesmi shodit celou frontu revizi — vypise se tedy aspon kod.
 */
function missingLabel(entry: AcceptanceMissingItem): string {
  try {
    return formatAcceptanceMissing(entry, 'cs');
  } catch {
    return entry.code;
  }
}
